import React from "react";
import { motion } from "framer-motion";
import { FiArrowLeft, FiLock, FiSearch } from "react-icons/fi";
import { MdVerified } from "react-icons/md";

// Get avatar gradient class based on name
const getAvatarGradient = (name) => {
  if (!name) return "grad-1";
  const code = name.charCodeAt(0) % 5;
  return `grad-${code + 1}`;
};

// Format lastSeen timestamp to "Active X ago" format
const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return null;

  const now = new Date();
  const lastSeenDate = new Date(lastSeen);
  const diffMs = now - lastSeenDate;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "Active just now";
  } else if (diffMinutes < 60) {
    return `Active ${diffMinutes} ${
      diffMinutes === 1 ? "minute" : "minutes"
    } ago`;
  } else if (diffHours < 24) {
    return `Active ${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  } else if (diffDays < 7) {
    return `Active ${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
  } else {
    // Format as date for older timestamps
    return `Active ${lastSeenDate.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    })}`;
  }
};

// Get status text based on presence
const getStatusText = (otherParticipant) => {
  if (!otherParticipant) return "Offline";

  if (otherParticipant.isOnline) {
    // Show custom status if available
    if (otherParticipant.statusMessage) {
      return otherParticipant.statusMessage;
    }
    // Show status like "away" or "busy" if not just "online"
    if (otherParticipant.status && otherParticipant.status !== "online") {
      return (
        otherParticipant.status.charAt(0).toUpperCase() +
        otherParticipant.status.slice(1)
      );
    }
    return "Online";
  }

  // Offline - show last seen if available
  const lastSeenText = formatLastSeen(
    otherParticipant.lastSeen || otherParticipant.lastActivity
  );

  return lastSeenText || "Offline";
};

const ChatHeader = ({
  conversation,
  onBack,
  isE2EEEnabled,
  encryptionReady,
  onSearchClick,
}) => {
  const otherParticipant = conversation?.otherParticipant;

  return (
    <motion.header
      className="msgx-chat-header"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Back Button (Mobile) */}
      <motion.button
        className="msgx-back-btn"
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <FiArrowLeft />
      </motion.button>

      {/* Avatar */}
      <div className="msgx-header-avatar">
        {otherParticipant?.profilePhotoUrl ? (
          <img
            src={otherParticipant.profilePhotoUrl}
            alt={otherParticipant?.name || "User"}
            className="msgx-header-avatar-img"
          />
        ) : (
          <div
            className={`msgx-header-avatar-initials ${getAvatarGradient(
              otherParticipant?.name
            )}`}
          >
            {otherParticipant?.name?.charAt(0).toUpperCase() || "?"}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="msgx-header-info">
        <h2 className="msgx-header-name">
          {otherParticipant?.name || "Unknown User"}
          {otherParticipant?.isVerified && (
            <MdVerified className="msgx-verified-badge" />
          )}
          {isE2EEEnabled && encryptionReady && (
            <FiLock
              className="msgx-encryption-badge"
              title="End-to-End Encrypted"
            />
          )}
        </h2>
        <div className="msgx-header-status">
          <span
            className={`msgx-header-status-dot ${
              otherParticipant?.isOnline ? "online" : "offline"
            }`}
          />
          <span>
            {getStatusText(otherParticipant)}
            {otherParticipant?.isOnline &&
              otherParticipant?.professionalRole && (
                <> · {otherParticipant.professionalRole}</>
              )}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="msgx-header-actions">
        {onSearchClick && (
          <motion.button
            className="msgx-header-action-btn"
            onClick={onSearchClick}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Search messages"
          >
            <FiSearch />
          </motion.button>
        )}
      </div>
    </motion.header>
  );
};

export default ChatHeader;
